/**
 * A NATUREZA DE CADA ITEM NO EDITOR DA ASSINATURA, e de onde vem o valor dele.
 * Prévia aprovada pelo Heitor em 05/10/2026: https://claude.ai/artifact/KzKn2krSKxjD6DkbRjtrxG
 *
 * Três naturezas, e só uma se digita:
 *  - `proprio`: a empresa contrata e paga sozinha. Edita tudo. Um desconto que NÃO está
 *    no rateio do grupo também é próprio: ele é da empresa, não do grupo.
 *  - `rateado`: a fatia desta empresa num produto que o grupo divide (`produtoIds`).
 *    DERIVADO: o valor sai da divisão. Digitar aqui cria exatamente a divergência
 *    ACORDADO × COBRADO que o invariante acusa com tolerância zero. Muda-se no rateio.
 *  - `desconto_do_grupo`: a fatia num desconto que o grupo divide. Derivado também, e
 *    tem ALVO (o item próprio em que abate), que continua sendo escolha desta tela.
 *
 * ⛔ TRÊS NÚMEROS, TRÊS ÉPOCAS (o que a prévia resolveu). Na CONCEITO, a linha do
 * CLOUD-SERVER mostrava "60,78% de 449,00" ao lado de 309,31, e 309,31 ÷ 449 = 68,89%.
 *  - 309,31 é o GRAVADO no item: o que a fatura cobra (68,89%, divisão de 31 de 45);
 *  - 60,78% é o peso de HOJE (31 de 51), que a versão 10/2026 já grava;
 *  - 311,39 é o ACORDADO da matriz na versão vigente, sobre o pool INTEIRO (512,30).
 * A linha mostra só o percentual que GEROU o valor dela (`origemDoValor`). O de hoje e
 * o acordado vão para o aviso do grupo (`desencontrosDoRateio`), comparados com a SOMA.
 */
import { ComRateio, RateioDoGrupo } from './rateioDoGrupo';

export type NaturezaDoItem = 'proprio' | 'rateado' | 'desconto_do_grupo';

interface ItemComValor {
  productId?: number | string | null;
  productCode?: string | null;
  totalValue?: number | string | null;
}

const num = (v: unknown) => {
  const n = parseFloat(String(v));
  return isFinite(n) ? n : 0;
};
const centavos = (v: number) => Math.round(v * 100);
/** Percentual em centésimos de ponto, a mesma granularidade do `divergente` do backend. */
const centesimos = (p: number) => Math.round(Number(p) * 100);

const comRateio = (r: RateioDoGrupo | null | undefined): ComRateio | null =>
  r && r.temRateio !== false ? (r as ComRateio) : null;

export function naturezaDoItem(item: ItemComValor, rateio: RateioDoGrupo | null | undefined): NaturezaDoItem {
  const r = comRateio(rateio);
  if (!r || item.productId == null || item.productId === '') return 'proprio';
  if (!(r.produtoIds || []).includes(Number(item.productId))) return 'proprio';
  const ehDesconto = num(item.totalValue) < 0 || /^DESCONTO/i.test(String(item.productCode || ''));
  return ehDesconto ? 'desconto_do_grupo' : 'rateado';
}

/** Rateado e desconto do grupo: o valor não se digita. */
export const valorTravado = (n: NaturezaDoItem) => n !== 'proprio';

export interface OrigemDoValor {
  /** O percentual que GEROU o valor gravado (total do item ÷ valor cheio do grupo). */
  percentual: number;
  /** Valor cheio do produto no grupo (negativo no desconto). */
  valorCheio: number;
  /** O percentual gravado é o da divisão vigente? */
  daDivisaoVigente: boolean;
}

/**
 * De onde vem o valor de um item rateado ou desconto do grupo. Null para item próprio,
 * ou quando o produto não está na divisão vigente do grupo.
 */
export function origemDoValor(item: ItemComValor, rateio: RateioDoGrupo | null | undefined, clientId: number): OrigemDoValor | null {
  const r = comRateio(rateio);
  if (!r || naturezaDoItem(item, r) === 'proprio') return null;
  const doGrupo = r.itens.vigente.find((i) => i.productId === Number(item.productId));
  if (!doGrupo || !doGrupo.valorCheio) return null;
  const percentual = Math.round((num(item.totalValue) / doGrupo.valorCheio) * 10000) / 100;
  const vigente = r.linhas.find((l) => l.clientId === clientId)?.percentualVigente;
  return {
    percentual,
    valorCheio: doGrupo.valorCheio,
    daDivisaoVigente: vigente != null && Math.abs(vigente - percentual) < 0.01,
  };
}

export interface ItensForaDaVersao {
  /** O que os itens do pool cobram hoje. */
  cobrado: number;
  /** O que a versão vigente registrou para a empresa. */
  acordado: number;
  /** cobrado − acordado (do backend: `deltaAcordadoVsCobrado`). */
  diferenca: number;
  percentualVigente: number;
  competenciaInicio: string | null;
}

export interface VersaoForaDaBase {
  percentualVigente: number;
  percentualCalculado: number;
  /** O que se cobra hoje (real, lido dos itens). */
  valorCobrado: number;
  /**
   * O que a base de hoje daria. ESTIMATIVA: é número computado, e o cobrado veio de
   * distribuição feita à mão (CLOUD-SERVER da CONCEITO: calculado 309,32, cobrado
   * 309,31). Não se compara com o cobrado por igualdade nem se mostra como certeza.
   */
  valorCalculadoEstimado: number;
  /** Ramais desta empresa e do grupo inteiro, na base de hoje. */
  ramais: number;
  ramaisDoGrupo: number;
}

export interface DesencontrosDoRateio {
  /** "O cobrado honra o acordo?" Não: resolve-se no rateio/motor. */
  itensForaDaVersao: ItensForaDaVersao | null;
  /** "A base mudou?" Sim: resolve-se no Redividir. */
  versaoForaDaBase: VersaoForaDaBase | null;
}

/**
 * Os dois desencontros da empresa no grupo, cada um com a SUA pergunta, ambas
 * respondidas pelo backend em centavos:
 *  - `deltaAcordadoVsCobrado` ≠ 0: o cobrado não honra o acordo. Na CONCEITO, +41,52
 *    (cobra 352,91, acorda 311,39). Null quando a versão não registrou o acordado:
 *    sem acordado não se afirma nada sobre o acordo.
 *  - `divergente`: a base de hoje daria outra divisão. É comparação de PERCENTUAL, feita
 *    pelo backend. ⛔ Até 05/10/2026 esta pergunta vinha de `deltaReais` ≠ 0, e dinheiro
 *    nunca foi detector: quando a divisão não muda, o backend copia o cobrado para o
 *    `valorCalculado` e o `deltaReais` vale zero por construção; quando muda, ele compara
 *    um cálculo com um valor distribuído à mão e pode errar por centavo. Às 17:02 de
 *    05/10 a CONCEITO tinha 60,78% acordado e 68,89% pela base, com `deltaReais` 0,01:
 *    o aviso dependia do arredondamento para aparecer.
 * Até o contrato novo a tela só sabia a segunda pergunta, e o `valorVigente` (que era o
 * cobrado) escondia a primeira. A tela mostra `versaoForaDaBase` primeiro.
 */
export function desencontrosDoRateio(rateio: RateioDoGrupo | null | undefined, clientId: number): DesencontrosDoRateio {
  const r = comRateio(rateio);
  const nada = { itensForaDaVersao: null, versaoForaDaBase: null };
  if (!r) return nada;
  const linha = r.linhas.find((l) => l.clientId === clientId);
  if (!linha) return nada;

  const dAcordo = linha.deltaAcordadoVsCobrado;
  const itensForaDaVersao = dAcordo != null && linha.valorAcordado != null && centavos(dAcordo) !== 0
    ? {
      cobrado: linha.valorCobrado,
      acordado: linha.valorAcordado,
      diferenca: centavos(dAcordo) / 100,
      percentualVigente: linha.percentualVigente,
      competenciaInicio: r.competenciaInicio,
    }
    : null;

  // `divergente` é do grupo; o aviso é desta empresa, então só quando o percentual DELA muda.
  // Fora do `divergente` o `valorCalculado` é o cobrado com outro nome: não entra em frase.
  const versaoForaDaBase = r.divergente === true && centesimos(linha.percentualVigente) !== centesimos(linha.percentualCalculado)
    ? {
      percentualVigente: linha.percentualVigente,
      percentualCalculado: linha.percentualCalculado,
      valorCobrado: linha.valorCobrado,
      valorCalculadoEstimado: linha.valorCalculado,
      ramais: Number(r.base.find((b) => b.clientId === clientId)?.ramais) || 0,
      ramaisDoGrupo: r.base.reduce((s, b) => s + (Number(b.ramais) || 0), 0),
    }
    : null;

  return { itensForaDaVersao, versaoForaDaBase };
}
